import React, { useEffect, useRef, useState } from "react";
import { Navigate, Link, useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { useI18n } from "../lib/i18n";
import { apiClient, formatApiError, formatSAR } from "../lib/api";
import { FEATURES } from "../lib/features";
import {
  MessageSquareWarning, ShieldCheck, ArrowRight, ArrowLeft, Loader2,
  AlertTriangle, Upload, X as CloseIcon, CheckCircle2, XCircle, Plus,
  Paperclip, Lock, Eye, Clock, Send, FileText,
} from "lucide-react";

/* ─── Status helpers ──────────────────────────────────────────────────── */
const STATUS_META = {
  open: { ar: "مفتوح — بانتظار رد", en: "Open — awaiting response", color: "#8a6f2e" },
  under_review: { ar: "قيد المراجعة", en: "Under review", color: "#1f4f8a" },
  awaiting_evidence: { ar: "بانتظار أدلة", en: "Awaiting evidence", color: "#8a6f2e" },
  mediating: { ar: "وساطة", en: "Mediating", color: "#1f4f8a" },
  resolution_proposed: { ar: "اقتراح حل", en: "Resolution proposed", color: "#0a4f3a" },
  escalated: { ar: "تصعيد", en: "Escalated", color: "#b91c1c" },
  resolved: { ar: "تم الحل", en: "Resolved", color: "#0a4f3a" },
  closed: { ar: "مغلق", en: "Closed", color: "#3a3a3a" },
  auto_closed: { ar: "مغلق تلقائياً", en: "Auto-closed", color: "#3a3a3a" },
  reopened: { ar: "أُعيد فتحه", en: "Reopened", color: "#8a6f2e" },
};
const CATEGORY_LABEL = {
  non_delivery: ["لم يُسلّم", "Non-delivery"],
  misrepresented: ["وصف غير دقيق", "Misrepresented"],
  defective: ["عيوب", "Defective"],
  payment_issue: ["مشكلة دفع", "Payment issue"],
  fraud: ["احتيال", "Fraud"],
  other: ["أخرى", "Other"],
};
const RESOLUTION_LABEL = {
  full_refund: ["استرداد كامل", "Full refund"],
  partial_refund: ["استرداد جزئي", "Partial refund"],
  replacement: ["استبدال", "Replacement"],
  other: ["أخرى", "Other"],
};

export function StatusBadge({ status, lang, size = "sm" }) {
  const meta = STATUS_META[status] || STATUS_META.open;
  const cls = size === "md" ? "text-xs px-3 py-1" : "text-[10px] px-2 py-0.5";
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full font-bold uppercase tracking-wider ${cls}`}
      style={{ backgroundColor: `${meta.color}18`, color: meta.color, border: `1px solid ${meta.color}40` }}
      data-testid={`dispute-status-${status}`}
    >
      {lang === "ar" ? meta.ar : meta.en}
    </span>
  );
}

/* ─── List page ────────────────────────────────────────────────────── */
export default function DisputesListPage() {
  const { user } = useAuth();
  const { lang } = useI18n();
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(true);

  useEffect(() => {
    if (!user) return;
    apiClient.get("/disputes/mine")
      .then(({ data }) => setItems(data.items || []))
      .finally(() => setBusy(false));
  }, [user]);

  if (user === undefined) return <div className="p-10 text-[#525252]">Loading…</div>;
  if (!user) return <Navigate to="/login" state={{ from: "/disputes" }} replace />;

  return (
    <div className="max-w-5xl mx-auto px-5 sm:px-8 py-10" data-testid="disputes-list-page">
      <div className="flex items-start justify-between flex-wrap gap-3 mb-3">
        <div>
          <div className="overline overline-gold mb-2 inline-flex items-center gap-2">
            <MessageSquareWarning className="w-3 h-3" /> {lang === "ar" ? "مركز النزاعات" : "Dispute Center"}
          </div>
          <h1 className="font-display text-3xl sm:text-4xl tracking-[-0.02em] font-medium">
            {lang === "ar" ? "نزاعاتي" : "My disputes"}
          </h1>
        </div>
        <Link to="/disputes/new" className="btn-primary" data-testid="dispute-new-btn">
          <Plus className="w-4 h-4" /> {lang === "ar" ? "فتح نزاع جديد" : "Open a new dispute"}
        </Link>
      </div>
      <p className="text-sm text-[#525252] mb-6 max-w-2xl">
        {lang === "ar"
          ? "كارتال طرف محايد. نسهّل التواصل، نتلقى الأدلة، ونساعد الطرفين على الوصول لاتفاق عادل. كل إجراء يُسجَّل في سجل قانوني."
          : "CARTAL acts as a neutral facilitator. We collect evidence, mediate between parties, and document every action in a legal audit trail."}
      </p>

      {busy ? (
        <div className="text-center py-12"><Loader2 className="w-5 h-5 animate-spin inline" /></div>
      ) : items.length === 0 ? (
        <div className="surface rounded-2xl p-12 text-center text-[#6b6b6b]" data-testid="dispute-empty">
          <ShieldCheck className="w-10 h-10 mx-auto text-[#c9a55c] mb-3" />
          {lang === "ar" ? "لا توجد نزاعات. نأمل ألا تحتاج هذه الصفحة أبداً." : "No disputes. We hope you never need this page."}
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((d) => (
            <Link key={d.id} to={`/disputes/${d.id}`} className="block surface rounded-2xl p-5 hover:shadow-md transition-shadow" data-testid={`dispute-card-${d.id}`}>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0 flex-1">
                  <div className="text-xs font-mono text-[#8a6f2e] mb-1">{d.code}</div>
                  <h3 className="font-display text-lg text-[#0a0a0a] truncate">{d.subject}</h3>
                  <div className="text-xs text-[#6b6b6b] mt-1">
                    {(CATEGORY_LABEL[d.category] || [d.category, d.category])[lang === "ar" ? 0 : 1]}
                    {d.amount_disputed_sar ? ` · ${formatSAR(d.amount_disputed_sar)} ${lang === "ar" ? "ريال" : "SAR"}` : ""}
                    {" · "}{new Date(d.created_at).toLocaleDateString(lang === "ar" ? "ar-SA" : "en-GB")}
                  </div>
                </div>
                <StatusBadge status={d.status} lang={lang} size="md" />
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

/* ─── New dispute (4-step wizard) ─────────────────────────────────────── */
export function NewDisputePage() {
  const { user } = useAuth();
  const { lang } = useI18n();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState(1);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [vehicles, setVehicles] = useState([]);
  const [form, setForm] = useState({
    vehicle_id: searchParams.get("vehicle_id") || "",
    conversation_id: searchParams.get("conversation_id") || "",
    escrow_payment_id: searchParams.get("escrow_id") || "",
    against_party_id: "",
    category: "",
    subject: "",
    description: "",
    amount_disputed_sar: "",
    desired_resolution: "",
    attachments: [],
  });
  const upd = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  useEffect(() => {
    if (!user) return;
    // Fetch user's recent vehicles + conversations to help linking
    apiClient.get("/vehicles?limit=30").then(({ data }) => setVehicles(data.items || []));
  }, [user]);

  if (user === undefined) return <div className="p-10 text-[#525252]">Loading…</div>;
  if (!user) return <Navigate to="/login" state={{ from: "/disputes/new" }} replace />;

  const linked = form.vehicle_id || form.conversation_id || form.escrow_payment_id;
  const step1Ok = !!linked;
  const step2Ok = form.category && form.subject.trim().length >= 4;
  const step3Ok = form.description.trim().length >= 10;
  const step4Ok = !!form.desired_resolution;

  const submit = async () => {
    setBusy(true); setErr(null);
    try {
      const payload = {
        vehicle_id: form.vehicle_id || null,
        conversation_id: form.conversation_id || null,
        escrow_payment_id: form.escrow_payment_id || null,
        category: form.category,
        subject: form.subject.trim(),
        description: form.description.trim(),
        amount_disputed_sar: form.amount_disputed_sar ? Number(form.amount_disputed_sar) : null,
        desired_resolution: form.desired_resolution,
        attachments: form.attachments,
      };
      const { data } = await apiClient.post("/disputes", payload);
      navigate(`/disputes/${data.id}`, { replace: true });
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="max-w-3xl mx-auto px-5 sm:px-8 py-10" data-testid="dispute-new-page">
      <div className="overline overline-gold mb-2 inline-flex items-center gap-2">
        <MessageSquareWarning className="w-3 h-3" /> {lang === "ar" ? "نزاع جديد" : "New dispute"}
      </div>
      <h1 className="font-display text-3xl sm:text-4xl tracking-[-0.02em] font-medium mb-6">
        {lang === "ar" ? "افتح نزاعاً" : "Open a dispute"}
      </h1>

      {/* Stepper */}
      <div className="flex items-center gap-2 mb-8" data-testid="dispute-stepper">
        {[1, 2, 3, 4].map((n) => (
          <React.Fragment key={n}>
            <div
              className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold transition-colors ${
                step === n ? "bg-[#0a0a0a] text-[#c9a55c]" : step > n ? "bg-[#c9a55c] text-[#0a0a0a]" : "bg-[#0a0a0a0d] text-[#6b6b6b]"
              }`}
            >
              {step > n ? "✓" : n}
            </div>
            {n < 4 && <div className={`flex-1 h-0.5 ${step > n ? "bg-[#c9a55c]" : "bg-[#0a0a0a14]"}`} />}
          </React.Fragment>
        ))}
      </div>

      {err && <div className="mb-4 rounded-xl p-3 bg-red-50 border border-red-200 text-red-700 text-sm" data-testid="dispute-error">{String(err)}</div>}

      {/* Step 1: Link */}
      {step === 1 && (
        <Step title={lang === "ar" ? "اربط النزاع بمعاملة" : "Link to a transaction"}
          help={FEATURES.ESCROW_ENABLED
            ? (lang === "ar" ? "النزاع يجب أن يكون مرتبطاً بسيارة أو محادثة أو دفعة Escrow في كارتال." : "Disputes must be tied to an existing vehicle, conversation, or escrow payment.")
            : (lang === "ar" ? "النزاع يجب أن يكون مرتبطاً بسيارة أو محادثة." : "Disputes must be tied to an existing vehicle or conversation.")}>
          <div>
            <label className="block text-xs font-semibold text-[#3a3a3a] mb-1.5">
              {lang === "ar" ? "اختر السيارة (إن وُجدت)" : "Pick a vehicle (if any)"}
            </label>
            <select className="input-dark" value={form.vehicle_id} onChange={(e) => upd("vehicle_id", e.target.value)} data-testid="dispute-vehicle-select">
              <option value="">{lang === "ar" ? "— لا شيء —" : "— None —"}</option>
              {vehicles.map((v) => (
                <option key={v.id} value={v.id}>{v.year} {v.make} {v.model} · {v.city}</option>
              ))}
            </select>
          </div>
          {FEATURES.ESCROW_ENABLED && (
          <div className="mt-4">
            <label className="block text-xs font-semibold text-[#3a3a3a] mb-1.5">
              {lang === "ar" ? "رقم دفعة الضمان (إن وُجد)" : "Escrow payment ID (if any)"}
            </label>
            <input className="input-dark" value={form.escrow_payment_id} onChange={(e) => upd("escrow_payment_id", e.target.value)} data-testid="dispute-escrow-id" />
          </div>
          )}
          <div className="mt-3 text-xs text-[#8a6f2e] inline-flex items-start gap-1">
            <AlertTriangle className="w-3.5 h-3.5 mt-0.5" />
            {FEATURES.ESCROW_ENABLED
              ? (lang === "ar" ? "يجب اختيار سيارة أو إدخال رقم دفعة الضمان للمتابعة." : "Pick a vehicle or enter an escrow payment ID to continue.")
              : (lang === "ar" ? "يجب اختيار سيارة للمتابعة." : "Pick a vehicle to continue.")}
          </div>
        </Step>
      )}

      {/* Step 2: Category + subject */}
      {step === 2 && (
        <Step title={lang === "ar" ? "صنّف النزاع" : "Categorise"}>
          <div>
            <label className="block text-xs font-semibold text-[#3a3a3a] mb-1.5">{lang === "ar" ? "فئة النزاع" : "Category"}</label>
            <div className="grid grid-cols-2 gap-2">
              {Object.entries(CATEGORY_LABEL).map(([k, v]) => (
                <button key={k} type="button" onClick={() => upd("category", k)}
                  className={`text-sm rounded-xl p-3 border-2 transition-colors text-start ${form.category === k ? "border-[#c9a55c] bg-[#fafaf7]" : "border-[#0a0a0a14] hover:border-[#c9a55c]/50"}`}
                  data-testid={`dispute-cat-${k}`}
                >
                  {v[lang === "ar" ? 0 : 1]}
                </button>
              ))}
            </div>
          </div>
          <div className="mt-4">
            <label className="block text-xs font-semibold text-[#3a3a3a] mb-1.5">{lang === "ar" ? "الموضوع" : "Subject"}</label>
            <input className="input-dark" value={form.subject} onChange={(e) => upd("subject", e.target.value)} required minLength={4} maxLength={200} data-testid="dispute-subject" />
          </div>
        </Step>
      )}

      {/* Step 3: Description + evidence */}
      {step === 3 && (
        <Step title={lang === "ar" ? "اشرح ما حدث وارفع الأدلة" : "Describe what happened & upload evidence"}>
          <label className="block text-xs font-semibold text-[#3a3a3a] mb-1.5">{lang === "ar" ? "الوصف التفصيلي" : "Detailed description"}</label>
          <textarea rows={6} className="input-dark" value={form.description} onChange={(e) => upd("description", e.target.value)} required minLength={10} maxLength={4000}
            placeholder={lang === "ar" ? "اشرح القصة بالتفصيل: ماذا حصل، متى، وما الذي تطلبه" : "Tell the full story: what happened, when, and what you are asking for"} data-testid="dispute-description" />
          <AttachmentUploader lang={lang} value={form.attachments} onChange={(arr) => upd("attachments", arr)} />
        </Step>
      )}

      {/* Step 4: Desired resolution */}
      {step === 4 && (
        <Step title={lang === "ar" ? "الحل المرغوب" : "Desired resolution"}>
          <div className="grid grid-cols-2 gap-2">
            {Object.entries(RESOLUTION_LABEL).map(([k, v]) => (
              <button key={k} type="button" onClick={() => upd("desired_resolution", k)}
                className={`text-sm rounded-xl p-3 border-2 transition-colors text-start ${form.desired_resolution === k ? "border-[#c9a55c] bg-[#fafaf7]" : "border-[#0a0a0a14] hover:border-[#c9a55c]/50"}`}
                data-testid={`dispute-res-${k}`}
              >
                {v[lang === "ar" ? 0 : 1]}
              </button>
            ))}
          </div>
          <div className="mt-4">
            <label className="block text-xs font-semibold text-[#3a3a3a] mb-1.5">{lang === "ar" ? "المبلغ المتنازع عليه (ريال، اختياري)" : "Disputed amount (SAR, optional)"}</label>
            <input className="input-dark" type="number" min="0" step="100" value={form.amount_disputed_sar} onChange={(e) => upd("amount_disputed_sar", e.target.value)} data-testid="dispute-amount" />
          </div>
          <div className="mt-5 text-xs text-[#525252] bg-[#fafaf7] border border-[#c9a55c]/30 rounded-xl p-3 leading-relaxed">
            <strong className="text-[#0a0a0a]">{lang === "ar" ? "ما يحدث بعد الإرسال:" : "What happens next:"}</strong>
            <ul className="mt-1 space-y-1 list-disc ms-4">
              <li>{lang === "ar" ? "يُخطَر الطرف الآخر فوراً ولديه 48 ساعة للرد." : "The other party is notified and has 48 hours to respond."}</li>
              {FEATURES.ESCROW_ENABLED && (
                <li>{lang === "ar" ? "إذا كان هناك Escrow مرتبط، يُجمَّد المبلغ تلقائياً." : "If escrow is linked, the funds are frozen automatically."}</li>
              )}
              <li>{lang === "ar" ? "وسيط من كارتال يدخل عند رد الطرف الآخر." : "A CARTAL mediator steps in once the counterparty responds."}</li>
            </ul>
          </div>
        </Step>
      )}

      {/* Nav */}
      <div className="flex justify-between mt-8">
        <button type="button" onClick={() => setStep((s) => Math.max(1, s - 1))} disabled={step === 1} className="btn-ghost text-sm disabled:opacity-50" data-testid="dispute-back">
          <ArrowLeft className="w-4 h-4 rtl:rotate-180" /> {lang === "ar" ? "السابق" : "Back"}
        </button>
        {step < 4 ? (
          <button type="button" onClick={() => setStep((s) => s + 1)}
            disabled={(step === 1 && !step1Ok) || (step === 2 && !step2Ok) || (step === 3 && !step3Ok)}
            className="btn-primary text-sm disabled:opacity-50" data-testid="dispute-next">
            {lang === "ar" ? "التالي" : "Next"} <ArrowRight className="w-4 h-4 rtl:rotate-180" />
          </button>
        ) : (
          <button type="button" onClick={submit} disabled={!step4Ok || busy} className="btn-primary text-sm disabled:opacity-50" data-testid="dispute-submit">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
            {lang === "ar" ? "إرسال النزاع" : "Submit dispute"}
          </button>
        )}
      </div>
    </div>
  );
}

function Step({ title, help, children }) {
  return (
    <div className="surface rounded-2xl p-6" data-testid={`step-${title}`}>
      <h2 className="font-display text-xl text-[#0a0a0a] mb-1">{title}</h2>
      {help && <p className="text-xs text-[#6b6b6b] mb-4">{help}</p>}
      {children}
    </div>
  );
}

function AttachmentUploader({ lang, value, onChange }) {
  const ref = useRef();
  const [uploading, setUploading] = useState(false);

  const handle = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      const uploaded = [];
      for (const f of files) {
        if (f.size > 8 * 1024 * 1024) continue;
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await apiClient.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        uploaded.push(data.url);
      }
      onChange([...value, ...uploaded].slice(0, 10));
    } finally { setUploading(false); e.target.value = ""; }
  };

  return (
    <div className="mt-4">
      <label className="block text-xs font-semibold text-[#3a3a3a] mb-1.5">{lang === "ar" ? "الأدلة (صور / PDF — حتى 10 ملفات)" : "Evidence (images / PDF — up to 10)"}</label>
      <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
        {value.map((url, i) => (
          <div key={url} className="relative rounded-lg border border-[#c9a55c]/40 bg-[#fafaf7] p-2 text-center">
            <Paperclip className="w-4 h-4 mx-auto text-[#8a6f2e] mb-1" />
            <span className="text-[10px] text-[#525252] truncate block">#{i + 1}</span>
            <button type="button" onClick={() => onChange(value.filter((_, idx) => idx !== i))}
              className="absolute -top-1 -end-1 w-5 h-5 rounded-full bg-red-600 text-white flex items-center justify-center text-[10px]" data-testid={`dispute-rm-attach-${i}`}>
              <CloseIcon className="w-3 h-3" />
            </button>
          </div>
        ))}
        {value.length < 10 && (
          <button type="button" onClick={() => ref.current?.click()}
            className="rounded-lg border-2 border-dashed border-[#c9a55c]/40 p-3 hover:border-[#c9a55c] hover:bg-[#fafaf7] flex flex-col items-center justify-center text-[#8a6f2e]" data-testid="dispute-add-attach">
            {uploading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Upload className="w-5 h-5" />}
            <span className="text-[10px] mt-1">{lang === "ar" ? "أضف" : "Add"}</span>
          </button>
        )}
      </div>
      <input ref={ref} type="file" multiple accept="image/*,application/pdf" className="hidden" onChange={handle} />
      <p className="text-[10px] text-[#8a8a8a] mt-1">{lang === "ar" ? "الأدلة محمية — يطلع عليها أطراف النزاع وفريق المراجعة فقط." : "Evidence is access-protected — only the parties and the review team can view it."}</p>
    </div>
  );
}

/* ─── Detail page ────────────────────────────────────────────────────── */
export function DisputeDetailPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const { lang } = useI18n();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(true);
  const [err, setErr] = useState(null);
  const [reply, setReply] = useState("");
  const [replyAttachments, setReplyAttachments] = useState([]);
  const [sending, setSending] = useState(false);
  const [responding, setResponding] = useState(false);

  const load = async () => {
    setBusy(true); setErr(null);
    try {
      const { data } = await apiClient.get(`/disputes/${id}`);
      setData(data);
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };
  useEffect(() => { if (user) load(); }, [user, id]);

  if (user === undefined) return <div className="p-10 text-[#525252]">Loading…</div>;
  if (!user) return <Navigate to="/login" state={{ from: `/disputes/${id}` }} replace />;

  const sendReply = async () => {
    if (!reply.trim()) return;
    setSending(true);
    try {
      await apiClient.post(`/disputes/${id}/messages`, { body: reply.trim(), attachments: replyAttachments });
      setReply(""); setReplyAttachments([]);
      await load();
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setSending(false); }
  };

  const respondOffer = async (accept) => {
    setResponding(true);
    try {
      await apiClient.post(`/disputes/${id}/respond-offer`, { accept });
      await load();
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setResponding(false); }
  };

  if (busy) return <div className="p-10 text-center"><Loader2 className="w-5 h-5 animate-spin inline" /></div>;
  if (err) return <div className="max-w-3xl mx-auto p-10"><div className="rounded-xl p-4 bg-red-50 border border-red-200 text-red-700">{String(err)}</div></div>;
  if (!data) return null;

  const d = data.dispute;
  const role = data.your_role;
  const isParty = role === "buyer" || role === "seller";
  const offer = d.resolution_offer;
  const myOfferResponse = role === "buyer" ? offer?.buyer_response : role === "seller" ? offer?.seller_response : null;
  const canRespondOffer = isParty && d.status === "resolution_proposed" && myOfferResponse === "pending";

  return (
    <div className="max-w-4xl mx-auto px-5 sm:px-8 py-10" data-testid="dispute-detail-page">
      <Link to="/disputes" className="text-xs text-[#8a6f2e] inline-flex items-center gap-1 hover:underline mb-2">
        <ArrowLeft className="w-3 h-3 rtl:rotate-180" /> {lang === "ar" ? "كل نزاعاتي" : "All my disputes"}
      </Link>

      <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
        <div className="min-w-0">
          <div className="text-xs font-mono text-[#8a6f2e] mb-1">{d.code}</div>
          <h1 className="font-display text-2xl sm:text-3xl tracking-[-0.02em] font-medium text-[#0a0a0a]">{d.subject}</h1>
          <div className="text-xs text-[#6b6b6b] mt-1">
            {(CATEGORY_LABEL[d.category] || [d.category, d.category])[lang === "ar" ? 0 : 1]}
            {d.amount_disputed_sar ? ` · ${formatSAR(d.amount_disputed_sar)} ${lang === "ar" ? "ريال" : "SAR"}` : ""}
            {" · "}{new Date(d.created_at).toLocaleString(lang === "ar" ? "ar-SA" : "en-GB")}
          </div>
        </div>
        <StatusBadge status={d.status} lang={lang} size="md" />
      </div>

      {/* Resolution offer card */}
      {offer && (
        <div className="rounded-2xl p-5 mb-5 border-2"
          style={{ backgroundColor: "#0a4f3a0d", borderColor: "#0a4f3a40" }}
          data-testid="dispute-offer">
          <h2 className="font-display text-lg text-[#0a4f3a] inline-flex items-center gap-2">
            <ShieldCheck className="w-5 h-5" /> {lang === "ar" ? "اقتراح حل من كارتال" : "CARTAL resolution offer"}
          </h2>
          <p className="text-sm text-[#3a3a3a] mt-2"><strong>{lang === "ar" ? "النوع: " : "Type: "}</strong>{(RESOLUTION_LABEL[offer.type] || [offer.type, offer.type])[lang === "ar" ? 0 : 1]}</p>
          {offer.amount_sar && <p className="text-sm text-[#3a3a3a]"><strong>{lang === "ar" ? "المبلغ: " : "Amount: "}</strong>{formatSAR(offer.amount_sar)} {lang === "ar" ? "ريال" : "SAR"}</p>}
          <p className="text-sm text-[#525252] mt-2">{offer.rationale}</p>
          <div className="mt-3 text-xs text-[#6b6b6b] flex flex-wrap gap-3">
            <span>{lang === "ar" ? "ردك:" : "Your response:"} <strong>{myOfferResponse || "—"}</strong></span>
            <span>{lang === "ar" ? "المهلة:" : "Deadline:"} <strong>{offer.response_deadline ? new Date(offer.response_deadline).toLocaleString(lang === "ar" ? "ar-SA" : "en-GB") : "—"}</strong></span>
          </div>
          {canRespondOffer && (
            <div className="flex gap-2 mt-4">
              <button onClick={() => respondOffer(true)} disabled={responding} className="btn-primary text-sm" data-testid="dispute-accept-offer">
                <CheckCircle2 className="w-4 h-4" /> {lang === "ar" ? "قبول" : "Accept"}
              </button>
              <button onClick={() => respondOffer(false)} disabled={responding} className="btn-ghost text-sm text-red-700 border-red-200" data-testid="dispute-reject-offer">
                <XCircle className="w-4 h-4" /> {lang === "ar" ? "رفض (يصعّد للأدمن)" : "Reject (escalates)"}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Final decision card */}
      {d.final_decision && (
        <div className="rounded-2xl p-5 mb-5 border-2"
          style={{ backgroundColor: "#fafaf7", borderColor: "rgba(201,165,92,0.40)" }}
          data-testid="dispute-decision">
          <h2 className="font-display text-lg text-[#8a6f2e] inline-flex items-center gap-2">
            <ShieldCheck className="w-5 h-5" /> {lang === "ar" ? "قرار نهائي" : "Final decision"}
          </h2>
          <p className="text-sm text-[#3a3a3a] mt-2"><strong>{lang === "ar" ? "النتيجة: " : "Outcome: "}</strong>{d.final_decision.outcome}</p>
          {d.final_decision.amount_sar && <p className="text-sm text-[#3a3a3a]"><strong>{lang === "ar" ? "المبلغ: " : "Amount: "}</strong>{formatSAR(d.final_decision.amount_sar)} {lang === "ar" ? "ريال" : "SAR"}</p>}
          <p className="text-sm text-[#525252] mt-2">{d.final_decision.rationale}</p>
          {d.final_decision.action_taken && d.final_decision.action_taken !== "none" && (
            <p className="text-xs text-[#0a4f3a] mt-2"><CheckCircle2 className="w-3 h-3 inline" /> {lang === "ar" ? "إجراء مُنفَّذ:" : "Action taken:"} <code>{d.final_decision.action_taken}</code></p>
          )}
        </div>
      )}

      {/* Messages thread */}
      <h2 className="font-display text-lg text-[#0a0a0a] mb-3">{lang === "ar" ? "سجل المحادثة" : "Conversation"}</h2>
      <div className="space-y-3 mb-5" data-testid="dispute-messages">
        {data.messages.map((m) => (
          <MessageBubble key={m.id} m={m} myUserId={user.id} disputeId={d.id} lang={lang} />
        ))}
      </div>

      {/* Reply box */}
      {!["closed", "auto_closed"].includes(d.status) && (
        <div className="surface rounded-2xl p-4" data-testid="dispute-reply">
          <textarea rows={3} className="input-dark" value={reply} onChange={(e) => setReply(e.target.value)}
            placeholder={lang === "ar" ? "اكتب ردك..." : "Write your reply..."} maxLength={4000} data-testid="dispute-reply-input" />
          <AttachmentUploader lang={lang} value={replyAttachments} onChange={setReplyAttachments} />
          <div className="flex justify-end mt-3">
            <button onClick={sendReply} disabled={sending || !reply.trim()} className="btn-primary text-sm disabled:opacity-50" data-testid="dispute-send-reply">
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              {lang === "ar" ? "إرسال" : "Send"}
            </button>
          </div>
        </div>
      )}

      {/* Reopen */}
      {["closed", "auto_closed", "resolved"].includes(d.status) && d.reopen_window_until && new Date(d.reopen_window_until) > new Date() && (
        <div className="rounded-2xl p-4 mt-5 bg-[#fef9ed] border border-[#c9a55c]/50 text-sm" data-testid="dispute-reopen-banner">
          <p className="text-[#8a6f2e] inline-flex items-center gap-2">
            <Clock className="w-4 h-4" />
            {lang === "ar" ? "لديك " : "You have until "}
            <strong>{new Date(d.reopen_window_until).toLocaleDateString(lang === "ar" ? "ar-SA" : "en-GB")}</strong>
            {lang === "ar" ? " لإعادة فتح النزاع مع دليل جديد." : " to reopen this dispute with new evidence."}
          </p>
        </div>
      )}

      <p className="text-[10px] text-[#a8a8a8] text-center mt-8 inline-flex items-center gap-1 justify-center w-full">
        <Lock className="w-3 h-3" />
        {lang === "ar" ? "كل إجراء يُسجَّل في سجل قانوني غير قابل للحذف." : "Every action is recorded in an immutable legal audit trail."}
      </p>
    </div>
  );
}

function MessageBubble({ m, myUserId, disputeId, lang }) {
  const mine = m.author_id === myUserId;
  const isAdmin = m.author_role === "admin" || m.author_role === "super_admin";
  const isInternal = m.is_internal;
  return (
    <div className={`flex ${mine ? "justify-end" : "justify-start"}`}>
      <div
        className={`max-w-[85%] rounded-2xl p-4 ${
          isInternal ? "bg-[#fef3c7] border border-[#f59e0b]/40" :
          isAdmin ? "bg-[#1f4f8a]/8 border border-[#1f4f8a]/30" :
          mine ? "bg-[#0a0a0a] text-white" : "bg-white border border-[#0a0a0a]/8"
        }`}
        data-testid={`dispute-msg-${m.id}`}
      >
        <div className="text-[10px] uppercase tracking-wider opacity-75 mb-1 flex items-center gap-1">
          {isInternal && <Lock className="w-3 h-3" />}
          {isInternal ? (lang === "ar" ? "ملاحظة داخلية" : "Internal note") : `${m.author_name || m.author_role} · ${m.author_role}`}
          {" · "}{new Date(m.created_at).toLocaleString(lang === "ar" ? "ar-SA" : "en-GB")}
        </div>
        <p className="text-sm whitespace-pre-wrap">{m.body}</p>
        {m.attachments?.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-2">
            {m.attachments.map((url, i) => (
              <AttachmentChip key={`${url}-${i}`} url={url} disputeId={disputeId} lang={lang} index={i} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function AttachmentChip({ url, disputeId, lang, index }) {
  // The raw URL points at /api/files/... — instead of leaking that public URL,
  // we route the click through the dispute's access-controlled proxy.
  const path = (url || "").replace(/^.*\/api\/files\//, "");
  const proxied = `${process.env.REACT_APP_BACKEND_URL}/api/disputes/${disputeId}/attachments/${path}`;
  return (
    <a href={proxied} target="_blank" rel="noopener noreferrer"
      className="inline-flex items-center gap-1 rounded-lg px-2 py-1 text-xs bg-white/90 border border-[#c9a55c]/40 hover:border-[#c9a55c] text-[#3a3a3a]"
      data-testid={`dispute-attachment-${disputeId}-${index}`}
    >
      <Paperclip className="w-3 h-3 text-[#8a6f2e]" />
      {lang === "ar" ? `دليل ${index + 1}` : `Evidence ${index + 1}`}
      <Eye className="w-3 h-3 opacity-60" />
    </a>
  );
}
